import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ScoreGauge from "@/components/ScoreGauge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, ChevronRight, Download, Loader2, Lock, RefreshCw, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PRIORITY_STYLES = {
  CRITIQUE: {
    bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/30", dot: "bg-rose-500",
  },
  IMPORTANT: {
    bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", dot: "bg-amber-500",
  },
  FAIBLE: {
    bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", dot: "bg-emerald-500",
  },
};

export default function Results() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [exporting, setExporting] = useState(false);

  const isPaid = user?.plan === "pro" || user?.plan === "agency";

  const handleExportPdf = async (lang = "fr") => {
    if (!isPaid) {
      toast.info("L'export PDF est réservé aux plans Pro et Agency.");
      navigate("/pricing");
      return;
    }
    setExporting(true);
    try {
      const res = await api.get(`/audits/${auditId}/pdf?lang=${lang}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `RevenueFix-Audit-${auditId}-${lang.toUpperCase()}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      try { localStorage.setItem("revenuefix.pdfLang", lang); } catch {}
    } catch (e) {
      // For blob responses, error body needs to be read as text
      let msg = "Impossible d'exporter le PDF.";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          const detail = j?.detail;
          msg = typeof detail === "string" ? detail : detail?.message || msg;
        } else {
          const detail = e?.response?.data?.detail;
          msg = typeof detail === "string" ? detail : detail?.message || msg;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get(`/audits/${auditId}`)
      .then((r) => setAudit(r.data))
      .catch(() => toast.error("Audit introuvable"))
      .finally(() => setLoading(false));
  }, [auditId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080A11] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
      </div>
    );
  }
  if (!audit) return null;

  const filtered = filter === "ALL" ? audit.issues : audit.issues.filter((i) => i.priority === filter);
  const critCount = audit.issues.filter((i) => i.priority === "CRITIQUE").length;
  const impCount = audit.issues.filter((i) => i.priority === "IMPORTANT").length;
  const faibleCount = audit.issues.filter((i) => i.priority === "FAIBLE").length;

  return (
    <div className="min-h-screen bg-[#080A11] text-slate-100">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono-alt text-slate-500 uppercase tracking-widest">Rapport d'audit</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-1">
              {audit.url.replace(/^https?:\/\//, "")}
            </h1>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap" data-testid="results-scan-meta">
              <span>{new Date(audit.created_at).toLocaleString("fr-FR")}</span>
              <span>·</span>
              <span>
                Mode:{" "}
                {audit.mode === "real_scan_ai"
                  ? "Scan réel + IA Claude Sonnet 5"
                  : audit.mode === "real_scan_ai_partial"
                  ? "Scan partiel (page JS-heavy) + IA"
                  : audit.mode === "ai"
                  ? "IA Claude Sonnet 5"
                  : "Heuristique MVP"}
              </span>
              {audit.scan_meta?.status_code && (
                <>
                  <span>·</span>
                  <span>HTTP {audit.scan_meta.status_code}</span>
                </>
              )}
              {audit.scan_meta?.response_time_ms != null && (
                <>
                  <span>·</span>
                  <span>{audit.scan_meta.response_time_ms} ms</span>
                </>
              )}
              {audit.scan_meta?.js_heavy && (
                <span className="ml-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                  Page JS-heavy
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isPaid ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="results-export-pdf-btn"
                    disabled={exporting}
                    variant="ghost"
                    className="h-10 border border-slate-700 text-slate-200 hover:bg-slate-800/60 hover:text-white"
                    title="Télécharger le rapport en PDF"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {exporting ? "Génération..." : "Exporter en PDF"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 text-slate-200">
                  <DropdownMenuItem
                    data-testid="results-export-pdf-fr"
                    onClick={() => handleExportPdf("fr")}
                    className="cursor-pointer focus:bg-slate-800 focus:text-white"
                  >
                    <span className="mr-2" aria-hidden>🇫🇷</span> Français
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="results-export-pdf-en"
                    onClick={() => handleExportPdf("en")}
                    className="cursor-pointer focus:bg-slate-800 focus:text-white"
                  >
                    <span className="mr-2" aria-hidden>🇬🇧</span> English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="results-export-pdf-btn"
                onClick={() => handleExportPdf("fr")}
                variant="ghost"
                className="h-10 border border-slate-800 text-slate-500 hover:text-violet-300 hover:bg-violet-500/5"
                title="Réservé aux plans Pro / Agency"
              >
                <Lock className="w-4 h-4 mr-2" />
                PDF (Pro)
              </Button>
            )}
            <Button
              data-testid="results-new-audit-btn"
              onClick={() => navigate("/analyze")}
              className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 border-0"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Nouvel audit
            </Button>
          </div>
        </div>

        {/* Score + Categories */}
        <div className="glass rounded-3xl p-8 sm:p-10 mb-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] glow-violet opacity-30" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="flex justify-center">
              <ScoreGauge score={audit.revenue_score} size={260} label="Revenue Score" />
            </div>
            <div>
              <p className="text-lg text-slate-200 leading-relaxed">{audit.summary}</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="text-center py-3 px-2 rounded-xl bg-rose-500/10 border border-rose-500/20">
                  <div className="font-display text-2xl font-bold text-rose-400">{critCount}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-rose-300 mt-1">Critique</div>
                </div>
                <div className="text-center py-3 px-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="font-display text-2xl font-bold text-amber-400">{impCount}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-300 mt-1">Important</div>
                </div>
                <div className="text-center py-3 px-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <div className="font-display text-2xl font-bold text-emerald-400">{faibleCount}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-300 mt-1">Faible</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category scores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {audit.scores.map((s) => {
            let color = "#8B5CF6";
            if (s.score < 50) color = "#EF4444";
            else if (s.score < 70) color = "#F59E0B";
            else if (s.score >= 85) color = "#10B981";
            return (
              <div key={s.category} data-testid={`category-score-${s.category.toLowerCase()}`} className="glass rounded-2xl p-5">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{s.category}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-3xl font-bold" style={{ color }}>{s.score}</span>
                  <span className="text-xs text-slate-500">/100</span>
                </div>
                <div className="text-xs mt-1" style={{ color }}>{s.level}</div>
                <p className="text-xs text-slate-400 mt-3 leading-relaxed">{s.explanation}</p>
              </div>
            );
          })}
        </div>

        {/* Priority issues */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Vos problèmes prioritaires</h2>
          <div className="flex items-center gap-2">
            {["ALL", "CRITIQUE", "IMPORTANT", "FAIBLE"].map((f) => (
              <button
                key={f}
                data-testid={`filter-severity-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-all ${
                  filter === f
                    ? "bg-violet-500/20 border-violet-500/50 text-violet-200"
                    : "bg-transparent border-slate-800 text-slate-500 hover:text-slate-300"
                }`}
              >
                {f === "ALL" ? "Tous" : f}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4 mb-16">
          {filtered.map((issue, idx) => {
            const style = PRIORITY_STYLES[issue.priority];
            return (
              <div
                key={idx}
                data-testid={`issue-card-${issue.priority.toLowerCase()}`}
                className={`glass rounded-2xl p-6 border-l-2 ${style.border}`}
              >
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <Badge className={`${style.bg} ${style.text} border ${style.border} rounded-full font-semibold text-[10px] uppercase tracking-widest px-2.5 py-1`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot} mr-1.5`} />
                    {issue.priority}
                  </Badge>
                  <span className="text-xs text-slate-500 font-mono-alt uppercase tracking-widest">{issue.category}</span>
                </div>
                <h3 className="font-display text-lg font-semibold text-white mb-3">{issue.title}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-slate-500 font-semibold uppercase tracking-widest text-[10px] block mb-1">Pourquoi</span>
                    <p className="text-slate-300 leading-relaxed">{issue.why}</p>
                  </div>
                  <div>
                    <span className="text-slate-500 font-semibold uppercase tracking-widest text-[10px] block mb-1">Recommandation</span>
                    <p className="text-slate-300 leading-relaxed">{issue.recommendation}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-800">
                    <span className="text-violet-400 font-semibold uppercase tracking-widest text-[10px] block mb-1">Action concrète</span>
                    <p className="text-slate-200 leading-relaxed">{issue.action}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Working */}
        <div className="glass rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Check className="w-4 h-4 text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white">Ce qui fonctionne déjà</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {audit.working.map((w, i) => (
                <div key={i} data-testid={`working-item-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-200">{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
